<template>
  <div id="requirementForm" class="form">
    <v-snackbar
      v-if="requirement.used"
      content-class="text-center"
      color="warning"
      :timeout="-1"
      width="100px"
      :value="true"
      absolute
      top
      centered
      >Requirement is in use</v-snackbar
    >

    <v-stepper v-model="step">
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" step="1">
          Requirement
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step step="2">
          Define rules {{ hasNext ? "" : "(Not Required)" }}
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-form
            v-if="step == 1 && loaded"
            ref="form"
            v-model="valid"
            lazy-validation
          >
            <v-text-field
              v-model="requirement.name"
              label="Name"
              :required="true"
              :rules="[rules.required]"
              lazy-validation
              placeholder="Assign a name to this requirement"
            />
            <v-select
              :disabled="requirement.used || editMode"
              v-model="selectedTargets"
              :items="contactTypes"
              label="Targeted Group"
              :multiple="!editMode"
              item-text="name"
              item-value="id"
              :chips="true"
              clearable
              :rules="[
                (v) => v > 0 || (v && v.length > 0) || 'This field is required',
              ]"
              required
              lazy-validation
              placeholder="Choose the applicable group for this requirement"
            ></v-select>

            <v-text-field
              v-model="requirement.description"
              label="Description"
              placeholder="Give a brief description of this requirement (optional)"
              :required="false"
            />

            <v-select
              :disabled="requirement.used"
              v-model="requirement.type"
              :items="types"
              :rules="[rules.required]"
              label="Requirement Type"
              @change="changedType"
            ></v-select>
            <div class="my-2" v-if="requirement.type == 'Required Documents' && !onlineForm">
              <label>Choose an online form (optional)</label>
              <v-btn @click="selectOnlinFormDialog = true" class="mx-2" text>
                Choose Form
              </v-btn>
            </div>
            <div v-else-if="requirement.type == 'Required Documents'">
              Selected online form <a class="ml-4" target="_blank" :href='formLink'>{{onlineForm.name}}</a> <v-btn @click="selectOnlinFormDialog = true" class="mx-2" text>
                Change Form
              </v-btn>
            </div>

            <div class="d-flex flex-column flex-lg-row">
              <v-radio-group v-model="requirement.required" row>
                <template v-slot:label>
                  <div>Required</div>
                </template>
                <v-radio :value="true">
                  <template v-slot:label>
                    <div>Yes</div>
                  </template>
                </v-radio>

                <v-radio :value="false">
                  <template v-slot:label>
                    <div>No</div>
                  </template>
                </v-radio>
              </v-radio-group>

              <v-divider class="mx-8" vertical />
              <v-radio-group v-model="requirement.attachmentRequired" row>
                <template v-slot:label>
                  <div>Require Proof</div>
                </template>
                <v-radio :value="true">
                  <template v-slot:label>
                    <div>Yes</div>
                  </template>
                </v-radio>

                <v-radio :value="false">
                  <template v-slot:label>
                    <div>No</div>
                  </template>
                </v-radio>
              </v-radio-group>

              <v-divider class="mx-8" vertical />
              <v-radio-group v-model="requirement.originalRequired" row>
                <template v-slot:label>
                  <div>Original Documents Required</div>
                </template>
                <v-radio :value="true">
                  <template v-slot:label>
                    <div>Yes</div>
                  </template>
                </v-radio>

                <v-radio :value="false">
                  <template v-slot:label>
                    <div>No</div>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>
            <div class="d-flex mt-3">
              <image-upload maxSize="52428800" v-model="files" />
            </div>

            <v-divider class="my-4"/>

            <div class="my-8 d-flex justify-space-between">
              <v-btn @click="goBack"> Back </v-btn>
              <v-btn @click="handleSubmit" color="primary">
                {{ hasNext ? "Next" : "Save" }}
              </v-btn>
              <span></span>
            </div>
          </v-form>
        </v-stepper-content>
        <v-stepper-content step="2">
          <div v-if="step == 2">
            <rule-certification-form
              v-if="requirement && requirement.type == 'Certification'"
              v-model="requirement.ruleCertification"
              :requirementIds="savedRequirementIds"
              @goBack="step = 1"
            />
            <rule-vaccination-form
              v-if="requirement && requirement.type == 'Vaccination'"
              v-model="requirement.ruleVaccination"
              :requirementIds="savedRequirementIds"
              @goBack="step = 1"
            />
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <div class="ma-4 text-caption text-weight-light" v-if="requirement.used">
      Notes: Used requirement have limited option to edit.
    </div>

    <v-dialog v-model="selectOnlinFormDialog">
      <v-card>
        <v-card-title class="justify-end">
          <div>
            <v-btn @click="selectOnlinFormDialog = null" icon
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </div>
        </v-card-title>
        <v-card-text class="d-flex">
          <select-form @selected="selectedOnlineForm" />
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { getRequirement } from "@/services/requirement";

import { getAllContactTypes } from "@/services/contactType";
import { updateStudentsCompleteStatus, updateStaffCompleteStatus } from '@/services/completeStatusService.js'

import { mapActions } from "vuex";
import Axios from "axios";

import { rules } from "@/store/util/rules.js";
import { parseDomain } from "parse-domain";

const ruleCertificationForm = () =>
  import("@/components/ruleCertification/RuleCertificationForm");
const ruleVaccinationForm = () =>
  import("@/components/ruleVaccination/RuleVaccinationForm");
const selectForm = () => import("@/components/forms/SelectForm");

export default {
  props: ["id"],
  data() {
    return {
      loaded: false,
      valid: false,
      step: 1,
      ruleId: null,
      contactTypes: [],
      types: ["Certification", "Required Documents", "Vaccination"],
      hasNext: true,
      selectedTargets: null,
      savedRequirementIds: null,
      selectOnlinFormDialog: false,
      onlineForm: null,
      requirement: {
        id: null,
        name: "",
        description: "",
        type: "",
        target: {},
        required: true,
        originalRequired: false,
        attachmentRequired: true,
        ruleCertification: null,
        ruleVaccination: null,
        used: false,
        formId: null,
      },
      rules,
      files: [],
    };
  },
  computed: {
    formLink() {
      if(this.onlineForm){
        if (process.env.NODE_ENV != "development") {
          const parseResult = parseDomain(window.location.hostname);
          const { domain, topLevelDomains } = parseResult;
          const topLevel = topLevelDomains.join(".");

          return `https://form.${domain}.${topLevel}/#/${this.onlineForm.accountName}/form/${this.onlineForm.id}`;
        } else {
          return `http://localhost:8086/#/${this.onlineForm.accountName}/form/${this.onlineForm.id}`;
        }
      }
      else{ return null }
    },
    editMode(){
      return this.id ? true : false;
    }
  },
  created() {
    this.setInstance();
  },
  methods: {
    ...mapActions("requirement", ["createRequirement", "updateRequirement"]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),

    async handleSubmit() {
      if (!this.$refs.form.validate()) {
        this.addErrorMessage("Please check your inputs");
        return;
      }
      if (this.id) {
        let requirement = this.requirement;
        requirement.target = this.selectedTargets;

        this.updateRequirement(requirement)
          .then((response) => {
            console.log(response);
            this.savedRequirementIds = [this.id];

            //upload files...
            this.uploadFiles(this.id, this.files);

            this.addSuccessMessage("Requirement was updated successfully");
            this.goNext();
          })
          .catch(() => {
            this.addErrorMessage(
              "Error updating Requirement, please try again later"
            );
          });
      } else {
        const requests = [];
        for (const target of this.selectedTargets) {
          let requirement = Object.assign({}, this.requirement);
          requirement.target = target;
          const createRequest = this.createRequirement(requirement);
          requests.push(createRequest);
        }
        Axios.all(requests)
          .then(
            Axios.spread((...responses) => {
              const createdRequirementIds = [];
              for (const response of responses) {
                createdRequirementIds.push(response);

                //upload files...
                this.uploadFiles(response, this.files);
              }

              this.savedRequirementIds = createdRequirementIds;
              this.addSuccessMessage("Requirement was created successfully");
              this.goNext();
            })
          )
          .catch(() => {
            this.addErrorMessage(
              "Error creating Requirement, please try again later"
            );
          });
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    goNext() {
      if (
        this.requirement.type == "Certification" &&
        !this.requirement.ruleCertification
      ) {
        this.requirement.ruleCertification = {
          requirementId: this.requirement.id,
        };
      }
      if (
        this.requirement.type == "Vaccination" &&
        !this.requirement.ruleVaccination
      ) {
        this.requirement.ruleVaccination = {
          requirementId: this.requirement.id,
        };
      }

      if (this.hasNext) {
        this.step = 2;
      } else {
        if (this.savedRequirementIds && this.savedRequirementIds.length == 1) {
          this.$router.push({
            name: "requirementView",
            params: { id: this.savedRequirementIds[0] },
          });
        } else {
          this.$router.push({ name: "requirement" });
        }
      }
    },
    updateCompleteStatus(){
      if(this.selectedTargets){
        let selectedContactTypes = [];
        if(Array.isArray(this.selectedTargets)){
          selectedContactTypes = this.contactTypes.filter(t => this.selectedTargets.find(s => s.id == t.id));
        }
        else{ 
            selectedContactTypes = this.contactTypes.filter(t => t.id == this.selectedTargets);
        }
        updateStudentsCompleteStatus(selectedContactTypes);
        updateStaffCompleteStatus(selectedContactTypes);
      }
    },
    changedType() {
      if (this.requirement.type == "Required Documents") {
        this.hasNext = false;
      } else {
        this.onlineForm = null;
        this.requirement.formId = null;
        this.hasNext = true;
      }
    },
    setInstance() {
      if (this.id) {
        this.requirement.id = this.id;
        getRequirement(this.id).then((response) => {
          this.requirement = response.data;

          if (this.requirement.type == "Required Documents") {
            this.hasNext = false;
          }

          this.selectedTargets = this.requirement.target;
          if(this.requirement.formId) {
            this.initForm();
          }

          this.loaded = true;
        });
      } else {
        this.loaded = true;
      }
      this.setDependencies();
    },
    setDependencies() {
      const params_target = {
        page: 0,
        size: 100,
      };
      getAllContactTypes(params_target).then((response) => {
        this.contactTypes = response.data.content;
      });
    },
    async uploadFiles(requirementId, files) {
      if (files && files.length > 0) {
        var formData = new FormData();
        for (const f of files) {
          formData.append("files", f, f.name);
        }

        this.addMessage("Uploading files...");
        return await this.$axios
          .post(`/requirement/upload/${requirementId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((resp) => {
            const savedFiles = resp.data;
            const uploadCount = savedFiles.files ? savedFiles.files.length : "";
            this.addSuccessMessage(
              `Uploaded ${uploadCount} files successfully`
            );
          });
      }
    },
    selectedOnlineForm(form){
      this.selectOnlinFormDialog = false;
      this.onlineForm = form;
      this.requirement.formId = form ? form.id:null;
    },
    async initForm() {
      if (this.requirement.formId) {
        const params = {
          form_id: this.requirement.formId,
        };
        return await this.$axios
          .get("/form/get-form", { params })
          .then((resp) => {
            this.onlineForm = resp.data.data;
          });
      }
    },
  },
  components: { ruleCertificationForm, ruleVaccinationForm, selectForm },
};
</script>
